import React, { useState } from 'react';
import { Card, Typography, Tag, Button, Space, Tooltip, List, Divider } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

// Inlined types — the customer portal cannot import from diagnosticService because
// that service uses a staff JWT which customers do not have.
type Verdict = 'green' | 'yellow' | 'red' | null;

export interface CustomerPortalCellResult {
  cell: string;
  value: number;
  verdict: Exclude<Verdict, null>;
  notes?: string;
}

export interface CustomerPortalSpecUsed {
  min: number;
  max?: number;
  source: string;
  attribution_text: string;
}

export interface CustomerPortalDiagnosticRun {
  id: string;
  procedure_id: number;
  vehicle_snapshot: any;
  spec_used: CustomerPortalSpecUsed | null;
  results: CustomerPortalCellResult[];
  overall_verdict: Verdict;
  completed_at: string | null;
}

export interface CustomerPortalDiagnosticProps {
  run: CustomerPortalDiagnosticRun;
  procedureName: string;
  units?: string;
  narrative?: string;
}

const VERDICT_COLOR: Record<string, string> = {
  green: 'success',
  yellow: 'warning',
  red: 'error',
};

export const CustomerPortalDiagnostic: React.FC<CustomerPortalDiagnosticProps> = ({
  run,
  procedureName,
  units,
  narrative,
}) => {
  const [downloading, setDownloading] = useState(false);
  const verdict = run.overall_verdict;
  const color = verdict ? VERDICT_COLOR[verdict] : 'default';
  const spec = run.spec_used ?? null;
  const maxBar = Math.max(
    spec?.max ?? 0,
    ...(run.results ?? []).map((c) => Number(c.value) || 0),
  );

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      // Lazy-load jsPDF only when the customer clicks Download — avoids pulling
      // the ~300 KB library into the initial bundle for every portal visit.
      // jsPDF v3 ships its class as the default export; cast to any to work
      // across CommonJS and ESM module resolution modes.
      const jsPDFModule: any = await import('jspdf');
      const JsPDF = jsPDFModule.jsPDF ?? jsPDFModule.default;
      const doc = new JsPDF({ unit: 'pt', format: 'letter' });

      const margin = 50;
      let y = margin;

      doc.setFontSize(16);
      doc.text(procedureName, margin, y);
      y += 22;

      doc.setFontSize(11);
      const verdictRgb =
        verdict === 'red'
          ? ([200, 18, 32] as [number, number, number])
          : verdict === 'yellow'
          ? ([180, 100, 0] as [number, number, number])
          : ([56, 158, 13] as [number, number, number]);
      doc.setTextColor(...verdictRgb);
      doc.text((verdict ?? 'unknown').toUpperCase(), margin, y);
      doc.setTextColor(40, 40, 40);
      y += 20;

      const v = run.vehicle_snapshot ?? {};
      doc.setFontSize(10);
      doc.text(
        `${v.year ?? ''} ${v.make ?? ''} ${v.model ?? ''} ${v.engine_size ?? ''} · ${run.completed_at ?? ''}`,
        margin,
        y,
      );
      y += 16;

      if (spec) {
        doc.text(
          `Spec: ${spec.min ?? '—'} – ${spec.max ?? '—'}${spec.attribution_text ? ' · ' + spec.attribution_text : ''}`,
          margin,
          y,
        );
        y += 20;
      }

      if (narrative) {
        const lines = doc.splitTextToSize(narrative, 500);
        doc.text(lines, margin, y);
        y += lines.length * 14 + 8;
      }

      doc.setFontSize(11);
      doc.text('Results:', margin, y);
      y += 16;

      (run.results ?? []).forEach((c) => {
        doc.setFontSize(10);
        const valueColor: [number, number, number] =
          c.verdict === 'red'
            ? [200, 18, 32]
            : c.verdict === 'yellow'
            ? [180, 100, 0]
            : [56, 158, 13];
        doc.setTextColor(...valueColor);
        doc.text(
          `${c.cell.toUpperCase().padEnd(8)} ${c.value} ${units ?? ''}  [${c.verdict}]`,
          margin + 10,
          y,
        );
        doc.setTextColor(40, 40, 40);
        y += 14;
      });

      doc.save(`diagnostic-${run.id}.pdf`);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('Customer portal PDF download failed', e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Text strong>{procedureName}</Text>
          {verdict && <Tag color={color as any}>{verdict.toUpperCase()}</Tag>}
        </Space>

        {narrative && (
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {narrative}
          </Paragraph>
        )}

        <div>
          {(run.results ?? []).map((c) => {
            const numericValue = Number(c.value) || 0;
            const pct = maxBar > 0 ? (numericValue / maxBar) * 100 : 0;
            const barColor =
              c.verdict === 'red'
                ? '#ff4d4f'
                : c.verdict === 'yellow'
                ? '#faad14'
                : '#52c41a';
            return (
              <div
                key={c.cell}
                style={{ display: 'flex', alignItems: 'center', margin: '4px 0' }}
              >
                <div style={{ width: 60, fontSize: 12 }}>{c.cell.toUpperCase()}</div>
                <div
                  style={{
                    flex: 1,
                    background: '#f5f5f5',
                    height: 14,
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ width: `${pct}%`, height: '100%', background: barColor }} />
                </div>
                <Tooltip title={units ?? ''}>
                  <div
                    style={{
                      width: 60,
                      fontSize: 12,
                      textAlign: 'right',
                      fontWeight: 600,
                    }}
                  >
                    {c.value}
                  </div>
                </Tooltip>
              </div>
            );
          })}
          {spec?.min != null && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Spec: {spec.min}
              {spec.max ? `–${spec.max}` : ''} {units ?? ''} · {spec.attribution_text}
            </Text>
          )}
        </div>

        <Button
          icon={<FilePdfOutlined />}
          onClick={handleDownloadPdf}
          loading={downloading}
        >
          Download PDF
        </Button>
      </Space>
    </Card>
  );
};

// ─── DTC codes section for customer portal ───────────────────────────────────
// Spec D-F-6: Customer portal shows resolved DTC codes + plain narrative.
// NO likely_causes shown to customer — only code, plain definition, status.

export type CustomerPortalDtcStatus = 'detected' | 'diagnosing' | 'resolved' | 'dismissed';

export interface CustomerPortalDtcCode {
  code: string;
  definition: string | null;
  status: CustomerPortalDtcStatus;
}

export interface CustomerPortalDtcSectionProps {
  dtcCodes: CustomerPortalDtcCode[];
  /** Optional plain-language narrative written by the technician */
  narrative?: string;
}

const DTC_STATUS_COLOR: Record<CustomerPortalDtcStatus, string> = {
  detected: 'orange',
  diagnosing: 'blue',
  resolved: 'green',
  dismissed: 'default',
};

/**
 * CustomerPortalDtcSection — shows resolved DTC codes + plain narrative.
 *
 * Intentionally simple: no likely_causes, no raw translation JSON.
 * Only what a customer needs to understand what was found + fixed.
 */
export const CustomerPortalDtcSection: React.FC<CustomerPortalDtcSectionProps> = ({
  dtcCodes,
  narrative,
}) => {
  if (!dtcCodes || dtcCodes.length === 0) return null;

  return (
    <Card
      size="small"
      title={<Text strong>Diagnostic Codes</Text>}
      style={{ marginTop: 12 }}
      data-testid="portal-dtc-section"
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {narrative && (
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {narrative}
          </Paragraph>
        )}
        <List
          size="small"
          dataSource={dtcCodes}
          renderItem={(dtc) => (
            <List.Item
              key={dtc.code}
              data-testid={`portal-dtc-item-${dtc.code}`}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{dtc.code}</Text>
                    <Tag color={DTC_STATUS_COLOR[dtc.status]} data-testid={`portal-dtc-status-${dtc.code}`}>
                      {dtc.status.charAt(0).toUpperCase() + dtc.status.slice(1)}
                    </Tag>
                  </Space>
                }
                description={
                  dtc.definition ? (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {dtc.definition}
                    </Text>
                  ) : null
                }
              />
            </List.Item>
          )}
        />
      </Space>
    </Card>
  );
};

/** @deprecated Re-exported for backwards compatibility. Use CustomerPortalDtcSection. */
export { Divider as _PortalDivider };
